import { useMemo, useState } from "react";
import { Aviso, Botao, Cartao, Segmentado } from "../componentes/ui";
import { DetalheDoCompromisso } from "../componentes/compromissos/DetalheDoCompromisso";
import { FormularioDeCompromisso } from "../componentes/compromissos/FormularioDeCompromisso";
import { SemanaDeCompromissos } from "../componentes/compromissos/SemanaDeCompromissos";
import { mensagemDoConsole } from "../nucleo/console";
import {
  corpoDaCriacao,
  formularioEmBranco,
  inicioDaSemana,
  intervaloDaSemana,
  rotuloDaSemana,
  somarDias,
  useCompromissos,
  useCriarCompromisso,
} from "../nucleo/compromissos";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";

type Quem = "meus" | "todos";

/** A agenda interna da equipe: semana em calendário, compromissos vinculados a chamado e cliente. */
export function AgendaDeCompromissos() {
  useTitulo("Compromissos");
  const sessao = useSessao();
  const permitido = podeNaSessao(sessao.data, "compromisso:gerenciar");
  const [hoje] = useState(() => new Date());
  const [inicio, setInicio] = useState(() => inicioDaSemana(hoje));
  const [quem, setQuem] = useState<Quem>("meus");
  const [criando, setCriando] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const criar = useCriarCompromisso();
  const voltarParaHome = useVoltarParaHome();
  const intervalo = useMemo(() => intervaloDaSemana(inicio), [inicio]);
  const meuId = sessao.data?.usuario.id ?? "";
  const lista = useCompromissos(
    { ...intervalo, responsavelId: quem === "meus" ? meuId : "" },
    permitido,
  );

  if (!sessao.data) {
    return (
      <p className="lead" role="status">
        Verificando o seu acesso…
      </p>
    );
  }
  if (!permitido) {
    return (
      <Aviso tom="marca" data-testid="agenda-sem-permissao">
        A agenda de compromissos é da equipe da GEOvendas.
      </Aviso>
    );
  }
  const escolhido = lista.data?.find((item) => item.id === aberto) ?? null;

  return (
    <div data-testid="agenda-de-compromissos">
      <h1 className="titulo-pagina">Compromissos</h1>
      <p className="lead mb-4">
        A agenda de trabalho da equipe: ligações, visitas e validações ligadas a um cliente, a um
        chamado ou a um ativo. As reuniões de implantação com o cliente ficam na agenda da
        implantação.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Botao
          variante="fantasma"
          aria-label="Semana anterior"
          onClick={() => setInicio((atual) => somarDias(atual, -7))}
        >
          ← Semana anterior
        </Botao>
        <Botao variante="fantasma" onClick={() => setInicio(inicioDaSemana(new Date()))}>
          Esta semana
        </Botao>
        <Botao
          variante="fantasma"
          aria-label="Próxima semana"
          onClick={() => setInicio((atual) => somarDias(atual, 7))}
        >
          Próxima semana →
        </Botao>
        <b data-testid="rotulo-da-semana">{rotuloDaSemana(inicio)}</b>
        <Segmentado<Quem>
          rotulo="De quem"
          valor={quem}
          aoMudar={setQuem}
          itens={[
            { valor: "meus", rotulo: "Meus" },
            { valor: "todos", rotulo: "Da equipe" },
          ]}
        />
        <Botao onClick={() => setCriando(true)} data-testid="novo-compromisso">
          Novo compromisso
        </Botao>
      </div>

      {criando ? (
        <Cartao titulo="Novo compromisso" className="mb-4">
          <FormularioDeCompromisso
            inicial={formularioEmBranco()}
            editando={false}
            enviando={criar.isPending}
            erroDoServidor={criar.error}
            aoCancelar={() => setCriando(false)}
            aoEnviar={(form) =>
              criar.mutate(corpoDaCriacao(form), {
                onSuccess: () => {
                  setCriando(false);
                  voltarParaHome("Compromisso agendado.");
                },
              })
            }
          />
        </Cartao>
      ) : null}

      {lista.isPending ? <p role="status">Carregando a semana…</p> : null}
      {lista.isError ? <Aviso tom="erro">{mensagemDoConsole(lista.error)}</Aviso> : null}
      {lista.data ? (
        <>
          <SemanaDeCompromissos
            inicio={inicio}
            compromissos={lista.data}
            hoje={hoje}
            selecionado={aberto}
            aoEscolher={setAberto}
          />
          {lista.data.length === 0 ? (
            <p className="mt-3" data-testid="semana-vazia">
              Nenhum compromisso nesta semana.
            </p>
          ) : null}
        </>
      ) : null}

      {escolhido ? (
        <div className="mt-4">
          <DetalheDoCompromisso
            key={escolhido.id}
            compromisso={escolhido}
            aoFechar={() => setAberto(null)}
            aoRemarcar={(remarcado) => setInicio(inicioDaSemana(new Date(remarcado.inicio_em)))}
          />
        </div>
      ) : null}
    </div>
  );
}
